import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { RootState } from 'src/store';
import { debounce } from 'ts-debounce';
import { useSurveysService } from '../services';
import { SurveySearchSortType } from 'src/modules/campaigns/types/SurveySearchSortType';

export const useSurveysViewController = () => {
  const currentCompanyId = useSelector(getCurrentCompanyId) as string;
  const {
    loading: surveysLoading,
    filter: surveysFilter,
    list: surveysList,
    getSurveys,
    changeSurveysFilter,
    resetSurveys,
    resetSurveysFilter,
  } = useSurveysService();

  // Prevent concurrent API calls and track initial load
  const isLoadingRef = useRef(false);
  const hasLoadedRef = useRef(false);
  const prevCompanyIdRef = useRef(currentCompanyId);

  // Reset loaded state when company changes
  useEffect(() => {
    if (prevCompanyIdRef.current !== currentCompanyId) {
      hasLoadedRef.current = false;
      prevCompanyIdRef.current = currentCompanyId;
    }
  }, [currentCompanyId]);

  const reloadSurveys = useCallback(async () => {
    try {
      await getSurveys(currentCompanyId);
    } catch (e) {
      console.log(e);
    }
  }, [getSurveys, currentCompanyId]);

  const reloadSurveysDebounce = useMemo(() => debounce(reloadSurveys, 500), [reloadSurveys]);

  const changeSurveyFilterForm = useCallback(async (filter: Partial<RootState["surveyResponses"]["filter"]>) => {
    changeSurveysFilter(filter);
    await reloadSurveys();
  }, [changeSurveysFilter, reloadSurveys]);

  const changeSurveysSearchFilter = useCallback(async (_: ChangeEvent<HTMLInputElement>, query: string) => {
    changeSurveysFilter({ query });
    await reloadSurveysDebounce();
  }, [changeSurveysFilter, reloadSurveysDebounce]);

  const changeSurveysSorting = useCallback(async (s: GridSortModel) => {
    const sortValue = s.length > 0 ? `${s[0].field}_${s[0].sort}` as SurveySearchSortType : 'updatedAt_desc';
    changeSurveysFilter({ sort: sortValue });
    await reloadSurveys();
  }, [changeSurveysFilter, reloadSurveys]);

  const changeSurveysPagination = useCallback(async (p: GridPaginationModel) => {
    changeSurveysFilter({ page: p.page, limit: p.pageSize, offset: p.page * p.pageSize });
    await reloadSurveys();
  }, [changeSurveysFilter, reloadSurveys]);

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      if (!isMounted || isLoadingRef.current || hasLoadedRef.current) return;

      isLoadingRef.current = true;
      hasLoadedRef.current = true;
      try {
        await getSurveys(currentCompanyId);
      } catch (e) {
        if (isMounted) {
          hasLoadedRef.current = false; // Allow retry on error
          console.log(e);
        }
      } finally {
        if (isMounted) {
          isLoadingRef.current = false;
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
      isLoadingRef.current = false;
      resetSurveys();

      // Keep the selected filter (time period, search, status, sorting) when navigating
      // to the survey details screen so it can inherit it. Reset it for any other navigation.
      const isNavigatingToDetails = window.location.pathname.startsWith('/survey-responses/');
      if (!isNavigatingToDetails) {
        resetSurveysFilter();
      }
    };
  }, [currentCompanyId, resetSurveys, resetSurveysFilter]);

  return {
    isPageLoading: surveysLoading.getList,
    surveysFilter,
    surveysList,
    changeSurveyFilterForm,
    changeSurveysSearchFilter,
    changeSurveysSorting,
    changeSurveysPagination,
  };
};